import React, { useEffect, useRef, useState } from "react";
import Modal from "@mui/material/Modal";
import { RxCross1 } from "react-icons/rx";
import {
  FormControl,
  MenuItem,
  Select,
  Tabs,
  Tab,
  Checkbox,
  ListItemIcon,
  ListItemText,
  TextField,
  Button,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { HttpService } from "../../../service/HttpService";
import moment from "moment/moment";
import { toast } from "react-toastify";
import { IoMdAdd } from "react-icons/io";

function AssignMappingModal({ open, onClose, scheduleTask, onAssigned }) {
  const httpService = new HttpService();
  // Default epicenters — this can be adjusted dynamically if needed
  const [epicenterKeys, setEpicenterKeys] = useState(["E1", "E2", "E3"]);
  const [epicenterTabIndex, setEpicenterTabIndex] = useState(0);
  const epicenterTab = epicenterKeys[epicenterTabIndex];

  // =========================================
  // OLD STATE (COMMENTED OUT)
  // =========================================
  const [tabData, setTabData] = useState(
    epicenterKeys.reduce((acc, key) => {
      acc[key] = [
        {
          __localId: `${key}-0`, // local id for UI keys
          cultivarId: "",
          trays: [],
          trayId: [],
          options: [],
          selected: [],
        },
      ];
      return acc;
    }, {}),
  );

  // NEW STATE
  // const [tabData, setTabData] = useState(
  //   epicenterKeys.reduce((acc, key) => {
  //     acc[key] = [
  //       {
  //         __localId: `${key}-0`,
  //         id: "",
  //         cultivarId: "",
  //         numberOfBuckets: "",
  //       },
  //     ];
  //     return acc;
  //   }, {}),
  // );

  const [cultivars, setCultivars] = useState([]);
  const [bucketCount, setBucketCount] = useState("");
  const [loadingMappings, setLoadingMappings] = useState(false);
  const formikRef = useRef(null);

  const roomName = scheduleTask?.schedule?.room_name_alias
    ? scheduleTask?.schedule?.room_name_alias
    : scheduleTask?.schedule?.room_name
      ? scheduleTask?.schedule?.room_name
      : "";
  const room_id = scheduleTask?.schedule?.room_id_alias;

  const validationSchema = Yup.object({
    // top-level validation is lightweight, detailed checks happen before submit
    dummy: Yup.string(),
  });

  // Helper: generate a new local id for a group
  const genLocalId = (prefix = "") =>
    `${prefix}${Date.now().toString(36)}${Math.random()
      .toString(36)
      .slice(2, 8)}`;

  // =========================================
  // OLD EFFECT (COMMENTED OUT)
  // =========================================
  useEffect(() => {
    if (!Array.isArray(epicenterKeys) || epicenterKeys.length === 0) return;

    setTabData((prev) => {
      const updated = { ...prev };

      epicenterKeys.forEach((key) => {
        if (!updated[key] || updated[key].length === 0) {
          updated[key] = [
            {
              __localId: `${key}-0`,
              cultivarId: "",
              trays: [],
              trayId: [],
              options: [],
              selected: [],
            },
          ];
        }
      });

      return updated;
    });
  }, [epicenterTab, open]);

  // NEW EFFECT
  // useEffect(() => {
  //   if (!Array.isArray(epicenterKeys) || epicenterKeys.length === 0) return;

  //   setTabData((prev) => {
  //     const updated = { ...prev };
  //     epicenterKeys.forEach((key) => {
  //       if (!updated[key] || updated[key].length === 0) {
  //         updated[key] = [
  //           {
  //             __localId: `${key}-0`,
  //             id: "",
  //             cultivarId: "",
  //             numberOfBuckets: "",
  //           },
  //         ];
  //       }
  //     });
  //     return updated;
  //   });
  // }, [epicenterTab, open]);

  // Fetch cultivars once
  useEffect(() => {
    fetchCultivars();
  }, []);

  const fetchCultivars = async () => {
    try {
      const res = await httpService.get("plant/get_plant");
      const raw = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
          ? res.data
          : [];
      const normalized = raw?.map((p) => ({
        id: String(p?.id ?? p?.plant_id ?? p?.value ?? p),
        name: String(p?.name ?? p?.plant_name ?? p?.label ?? p?.id ?? p),
      }));
      setCultivars(normalized);
    } catch (e) {
      setCultivars([]);
      console.error("fetchCultivars error", e);
    }
  };

  // =========================================
  // OLD CODE - TRAY & CLONE API HANDLERS (COMMENTED OUT)
  // =========================================
  // Fetch trays for a cultivar for a specific group
  const fetchTrays = async (cultivarId, epicenter, groupIndex) => {
    if (!cultivarId) {
      setTabData((prev) => {
        const copy = { ...prev };
        const groups = [...(copy[epicenter] || [])];
        groups[groupIndex] = {
          ...groups[groupIndex],
          trays: [],
          trayId: [],
          options: [],
          selected: [],
        };
        copy[epicenter] = groups;
        return copy;
      });
      return;
    }
    try {
      const res = await httpService.get(`mapping/plant/${cultivarId}`);
      const list = Array.isArray(res?.data) ? res.data : [];
      setTabData((prev) => {
        const copy = { ...prev };
        const groups = [...(copy[epicenter] || [])];
        groups[groupIndex] = { ...groups[groupIndex], trays: list };
        copy[epicenter] = groups;
        return copy;
      });
    } catch (e) {
      console.error("fetchTrays error", e);
      setTabData((prev) => {
        const copy = { ...prev };
        const groups = [...(copy[epicenter] || [])];
        groups[groupIndex] = { ...groups[groupIndex], trays: [] };
        copy[epicenter] = groups;
        return copy;
      });
    }
  };

  // Fetch options (clones) for given trayIds for a specific group
  // trayIds is an array of strings
  const fetchOptions = async (trayIds = [], epicenter, groupIndex) => {
    if (!Array.isArray(trayIds) || trayIds.length === 0) {
      setTabData((prev) => {
        const copy = { ...prev };
        const groups = [...(copy[epicenter] || [])];
        groups[groupIndex] = {
          ...groups[groupIndex],
          options: [],
          selected: [],
        };
        copy[epicenter] = groups;
        return copy;
      });
      return [];
    }
    try {
      // If API expects CSV tray ids in path, join them; adjust if your API accepts query param
      const trayParam = trayIds.join(",");
      const res = await httpService.get(
        `mapping/clones/${trayParam}?schedule_task_id=${scheduleTask?.id}`,
      );
      const list = Array.isArray(res?.data?.clone_ids)
        ? res.data.clone_ids
        : Array.isArray(res?.data)
          ? res.data
          : [];

      setTabData((prev) => {
        const copy = { ...prev };
        const groups = [...(copy[epicenter] || [])];
        groups[groupIndex] = { ...groups[groupIndex], options: list };
        copy[epicenter] = groups;
        return copy;
      });
      return list;
    } catch (e) {
      console.error("fetchOptions error", e);
      setTabData((prev) => {
        const copy = { ...prev };
        const groups = [...(copy[epicenter] || [])];
        groups[groupIndex] = {
          ...groups[groupIndex],
          options: [],
          selected: [],
        };
        copy[epicenter] = groups;
        return copy;
      });
      return [];
    }
  };

  const getBucketCount = async () => {
    try {
      const res = await httpService.get(
        `mapping/buckets/count?epicenter=${epicenterTab}&room_id=${room_id}`,
      );
      const list = res?.data;
      setBucketCount(list?.count ?? "");
    } catch (_) {
      setBucketCount("");
    }
  };

  useEffect(() => {
    getBucketCount();
  }, [epicenterTab]);

  // =========================================
  // OLD CODE - CLONE CONSTRAINTS (COMMENTED OUT)
  // =========================================
  // Utility: get set of clone IDs already selected in other epicenters/groups
  const getUsedCloneIds = () => {
    const used = new Set();
    Object.keys(tabData).forEach((ep) => {
      tabData[ep].forEach((group) => {
        (group.selected || []).forEach((id) => used.add(id));
      });
    });
    return used;
  };

  // =========================================
  // OLD CULTIVAR GROUP HANDLERS (COMMENTED OUT)
  // =========================================
  // Add/Remove cultivar group in an epicenter
  const addCultivarGroup = (epicenter) => {
    const newGroup = {
      __localId: genLocalId(`${epicenter}-`),
      cultivarId: "",
      trays: [],
      trayId: [],
      options: [],
      selected: [],
    };
    setTabData((prev) => ({
      ...prev,
      [epicenter]: [...(prev[epicenter] || []), newGroup],
    }));
  };

  // NEW CULTIVAR GROUP HANDLERS
  // const addCultivarGroup = (epicenter) => {
  //   const newGroup = {
  //     __localId: genLocalId(`${epicenter}-`),
  //     id: "",
  //     cultivarId: "",
  //     numberOfBuckets: "",
  //   };
  //   setTabData((prev) => ({
  //     ...prev,
  //     [epicenter]: [...(prev[epicenter] || []), newGroup],
  //   }));
  // };

  const removeCultivarGroup = (epicenter, index) => {
    setTabData((prev) => {
      const copy = { ...prev };
      const groups = [...(copy[epicenter] || [])];
      groups.splice(index, 1);
      copy[epicenter] = groups.length ? groups : [];
      return copy;
    });
  };

  // =========================================
  // OLD CONFLICT TOGGLES (COMMENTED OUT)
  // =========================================
  // When user toggles a clone checkbox for a specific group
  const toggleCloneForGroup = (epicenter, groupIndex, cloneId) => {
    setTabData((prev) => {
      const copy = { ...prev };
      const groups = [...(copy[epicenter] || [])];
      const group = { ...groups[groupIndex] };
      const selected = Array.isArray(group.selected) ? [...group.selected] : [];
      const exists = selected.includes(cloneId);
      group.selected = exists
        ? selected.filter((id) => id !== cloneId)
        : [...selected, cloneId];
      groups[groupIndex] = group;
      copy[epicenter] = groups;
      return copy;
    });
  };

  // When tray selection changes for a specific group:
  // 1) fetch options for new trayIds
  // 2) keep only selected clone ids whose plant_mother_task_id matches one of the selected trayIds
  const onGroupTrayChange = async (epicenter, groupIndex, newTrayIds) => {
    // normalize tray IDs as strings
    const selectedTrayIds = Array.from(new Set((newTrayIds || []).map(String)));

    // fetch new clone options for selected trays
    const newOptions = await fetchOptions(
      selectedTrayIds,
      epicenter,
      groupIndex,
    );

    setTabData((prev) => {
      const copy = { ...prev };
      const groups = [...(copy[epicenter] || [])];
      const group = { ...groups[groupIndex] };

      // previous selected clone IDs
      const prevSelected = Array.isArray(group.selected)
        ? [...group.selected]
        : [];
      const prevOptions = Array.isArray(group.options) ? group.options : [];

      // Build lookup map of all known options (old + new)
      const lookup = {};
      [...newOptions, ...prevOptions].forEach((o) => {
        lookup[String(o.id)] = o;
      });

      // Keep only selected clones whose tray still exists in selectedTrayIds
      const filteredSelected = prevSelected.filter((selId) => {
        const opt = lookup[String(selId)];
        if (!opt) return false;
        return selectedTrayIds.includes(String(opt.plant_mother_task_id));
      });

      // ✅ Update this group
      group.trayId = selectedTrayIds;
      group.options = newOptions;
      group.selected = filteredSelected;

      groups[groupIndex] = group;
      copy[epicenter] = groups;
      return copy;
    });
  };

  // =========================================
  // OLD CULTIVAR SELECT CHANGE (COMMENTED OUT)
  // =========================================
  const onGroupCultivarChange = (epicenter, groupIndex, cultivarId) => {
    setTabData((prev) => {
      const copy = { ...prev };
      const groups = [...(copy[epicenter] || [])];
      groups[groupIndex] = {
        ...groups[groupIndex],
        cultivarId: cultivarId,
        trays: [],
        trayId: [],
        options: [],
        selected: [],
      };
      copy[epicenter] = groups;
      return copy;
    });
    if (cultivarId) fetchTrays(cultivarId, epicenter, groupIndex);
  };

  // NEW CULTIVAR SELECT CHANGE
  // const onGroupCultivarChange = (epicenter, groupIndex, cultivarId) => {
  //   setTabData((prev) => {
  //     const copy = { ...prev };
  //     const groups = [...(copy[epicenter] || [])];
  //     groups[groupIndex] = {
  //       ...groups[groupIndex],
  //       cultivarId: cultivarId,
  //     };
  //     copy[epicenter] = groups;
  //     return copy;
  //   });
  // };

  // NEW BUCKETS INPUT CHANGE
  // const onGroupBucketsChange = (epicenter, groupIndex, value) => {
  //   setTabData((prev) => {
  //     const copy = { ...prev };
  //     const groups = [...(copy[epicenter] || [])];
  //     groups[groupIndex] = {
  //       ...groups[groupIndex],
  //       numberOfBuckets: value === "" ? "" : Number(value),
  //     };
  //     copy[epicenter] = groups;
  //     return copy;
  //   });
  // };

  // =========================================
  // OLD MAPPING ASSIGN CALL (COMMENTED OUT)
  // =========================================
  const handleAssign = async () => {
    // validate at least one selection exists
    const epicenterData = [];
    Object.keys(tabData).forEach((epicenter) => {
      tabData[epicenter].forEach((group) => {
        if (group.selected && group.selected.length > 0 && group.cultivarId) {
          epicenterData.push({
            epicenter,
            clone_ids: group.selected,
            cultivar_id: group.cultivarId,
            tray_ids: group.trayId,
          });
        }
      });
    });

    if (epicenterData.length === 0) {
      toast.error(
        "No data to submit. Please make selections in at least one group.",
        {
          position: "top-right",
        },
      );
      return;
    }

    try {
      const payload = {
        schedule_task_id: scheduleTask?.id,
        epicenter_data: epicenterData,
        room_id: scheduleTask?.schedule?.room_id_alias,
        date_of_mapping: moment(new Date()).format("MM-DD-YYYY"),
      };
      await httpService.post(`mapping`, payload);
      toast.success("Mapping assigned successfully!", {
        position: "top-right",
      });
      onAssigned && onAssigned();
      onClose();
    } catch (e) {
      console.error("assign error", e);
      toast.error("Failed to assign mapping");
    }
  };

  // NEW MAPPING ASSIGN CALL (POST mapping/cultivars)
  // const handleAssign = async () => {
  //   // Validation: check for half-filled rows
  //   let hasValidationError = false;
  //   Object.keys(tabData).forEach((epicenter) => {
  //     (tabData[epicenter] || []).forEach((group) => {
  //       if (
  //         (group.cultivarId && !group.numberOfBuckets) ||
  //         (!group.cultivarId && group.numberOfBuckets)
  //       ) {
  //         hasValidationError = true;
  //       }
  //     });
  //   });

  //   if (hasValidationError) {
  //     toast.error(
  //       "Please complete all rows by selecting both a cultivar and entering the number of buckets.",
  //       {
  //         position: "top-right",
  //       },
  //     );
  //     return;
  //   }

  //   const payloadCultivars = [];
  //   Object.keys(tabData).forEach((epicenter) => {
  //     (tabData[epicenter] || []).forEach((group) => {
  //       if (group.cultivarId && group.numberOfBuckets) {
  //         payloadCultivars.push({
  //           id: group.id ? Number(group.id) : null,
  //           plant_id: Number(group.cultivarId),
  //           epicenter: epicenter,
  //           number_of_buckets: Number(group.numberOfBuckets),
  //         });
  //       }
  //     });
  //   });

  //   try {
  //     const payload = {
  //       schedule_task_id: scheduleTask?.id,
  //       cultivars: payloadCultivars,
  //     };
  //     await httpService.post(`mapping/cultivars`, payload);
  //     toast.success("Mapping assigned successfully!", {
  //       position: "top-right",
  //     });
  //     onAssigned && onAssigned();
  //     onClose();
  //   } catch (e) {
  //     console.error("assign error", e);
  //     toast.error(e?.response?.data?.message || "Failed to assign mapping");
  //   }
  // };

  // UI helpers
  const handleTabChange = (_, newIndex) => {
    setEpicenterTabIndex(newIndex);
  };

  // =========================================
  // OLD MODAL OPEN TRIGGER (COMMENTED OUT)
  // =========================================
  // If modal opens freshly, reset default data and fetch existing mappings if needed
  useEffect(() => {
    if (!open) return;
    // init tabData if empty / when open
    setEpicenterTabIndex(0);
    // keep existing epicenterKeys; if you want to derive from server, change this block
    setTabData((prev) => {
      const copy = { ...prev };
      epicenterKeys.forEach((k) => {
        if (!Array.isArray(copy[k]) || copy[k].length === 0) {
          copy[k] = [
            {
              __localId: genLocalId(`${k}-`),
              cultivarId: "",
              trays: [],
              trayId: [],
              options: [],
              selected: [],
            },
          ];
        }
      });
      return copy;
    });
    fetchExistingMappings();
    // optionally fetch existing mappings to pre-populate (not implemented here)
  }, [open]);

  // NEW MODAL OPEN TRIGGER
  // useEffect(() => {
  //   if (!open) return;
  //   setEpicenterTabIndex(0);
  //   setTabData((prev) => {
  //     const copy = { ...prev };
  //     epicenterKeys.forEach((k) => {
  //       if (!Array.isArray(copy[k]) || copy[k].length === 0) {
  //         copy[k] = [
  //           {
  //             __localId: genLocalId(`${k}-`),
  //             id: "",
  //             cultivarId: "",
  //             numberOfBuckets: "",
  //           },
  //         ];
  //       }
  //     });
  //     return copy;
  //   });
  //   fetchExistingMappings();
  // }, [open]);

  // =========================================
  // OLD FETCH EXISTING MAPPINGS (COMMENTED OUT)
  // =========================================
  const fetchExistingMappings = async () => {
    if (!scheduleTask?.id) return;
    setLoadingMappings(true);

    try {
      const res = await httpService.get(`mapping/${scheduleTask?.id}`);
      const data = res?.data || {};
      const mappingDetails = Array.isArray(data.mapping_details)
        ? data.mapping_details
        : [];
      const cloneDetails = Array.isArray(data.clone_details)
        ? data.clone_details
        : [];

      if (mappingDetails.length === 0) {
        setLoadingMappings(false);
        return;
      }

      // Group data by epicenter -> plant_id -> tray_id
      const grouped = {};

      mappingDetails.forEach((m) => {
        const epicenter = m.epicenter || "E1";
        const plantId = String(m.plant_id);
        const trayId = String(m.tray_id);
        const cloneId = String(m.clone_id);

        if (!grouped[epicenter]) grouped[epicenter] = {};
        if (!grouped[epicenter][plantId]) grouped[epicenter][plantId] = {};
        if (!grouped[epicenter][plantId][trayId])
          grouped[epicenter][plantId][trayId] = {
            trayId,
            clones: [],
          };

        const cloneInfo = cloneDetails.find((c) => String(c.id) === cloneId);
        if (cloneInfo) {
          grouped[epicenter][plantId][trayId].clones.push({
            id: cloneInfo.id,
            clone_id: cloneInfo.clone_id,
          });
        }
      });

      // Convert grouped structure into your tabData format
      const newTabData = {};

      Object.keys(grouped).forEach((epicenter) => {
        newTabData[epicenter] = [];

        Object.keys(grouped[epicenter]).forEach((plantId) => {
          Object.keys(grouped[epicenter][plantId]).forEach((trayId) => {
            const trayGroup = grouped[epicenter][plantId][trayId];
            const matchingClones = cloneDetails.filter(
              (clone) => parseInt(clone.tray_id) === parseInt(trayId),
            );
            if (!trayGroup) return;
            newTabData[epicenter].push({
              __localId: genLocalId(`${epicenter}-`),
              cultivarId: plantId,
              trays: [], // you can load actual tray info later if needed
              trayId: [trayId],
              options: matchingClones,
              selected: trayGroup.clones.map((c) => c.id),
            });
          });
        });
      });

      setTabData(newTabData);

      // Step 4: Fetch trays per group (populate tray names/options)
      for (const [epicenter, groups] of Object.entries(newTabData)) {
        for (let i = 0; i < groups.length; i++) {
          const group = groups[i];
          if (group.cultivarId) {
            await fetchTrays(group.cultivarId, epicenter, i);
          }
        }
      }
    } catch (e) {
      console.error("fetchExistingMappings error", e);
    } finally {
      setLoadingMappings(false);
    }
  };

  // NEW FETCH EXISTING MAPPINGS (Loads allocations from plant_mapping_cultivars)
  // const fetchExistingMappings = async () => {
  //   if (!scheduleTask?.id) return;
  //   setLoadingMappings(true);

  //   try {
  //     const res = await httpService.get(`mapping/cultivars/${scheduleTask?.id}`);
  //     const allocations = Array.isArray(res?.data)
  //       ? res.data
  //       : [];

  //     // Group allocations by epicenter
  //     const groupedByEpicenter = {};
  //     allocations.forEach((item) => {
  //       const ep = item.epicenter || "E1";
  //       if (!groupedByEpicenter[ep]) {
  //         groupedByEpicenter[ep] = [];
  //       }
  //       groupedByEpicenter[ep].push({
  //         id: String(item.id),
  //         cultivarId: String(item.plant_id),
  //         numberOfBuckets: Number(item.number_of_buckets || 0),
  //         __localId: genLocalId(`${ep}-`),
  //       });
  //     });

  //     // Populate tabData format
  //     const newTabData = {};
  //     epicenterKeys.forEach((key) => {
  //       const epicAllocations = groupedByEpicenter[key] || [];
  //       if (epicAllocations.length > 0) {
  //         newTabData[key] = epicAllocations;
  //       } else {
  //         newTabData[key] = [
  //           {
  //             __localId: genLocalId(`${key}-`),
  //             id: "",
  //             cultivarId: "",
  //             numberOfBuckets: "",
  //           },
  //         ];
  //       }
  //     });

  //     setTabData(newTabData);
  //   } catch (e) {
  //     console.error("fetchExistingMappings error", e);
  //   } finally {
  //     setLoadingMappings(false);
  //   }
  // };

  // NEW: Get assigned count
  // const getAssignedCount = () => {
  //   const groups = tabData[epicenterTab] || [];
  //   return groups.reduce((sum, g) => sum + (Number(g.numberOfBuckets) || 0), 0);
  // };

  // const currentAssigned = getAssignedCount();
  // const currentCapacity = Number(bucketCount) || 0;
  // const isOverCapacity = currentAssigned > currentCapacity;

  // const getIsDisabled = () => {
  //   if (isOverCapacity) return true;

  //   let totalAssignedAnywhere = 0;
  //   let hasHalfFilled = false;

  //   Object.keys(tabData).forEach((ep) => {
  //     const groups = tabData[ep] || [];
  //     groups.forEach((group) => {
  //       const hasCultivar = !!group.cultivarId;
  //       const hasBuckets =
  //         group.numberOfBuckets !== "" &&
  //         group.numberOfBuckets !== null &&
  //         Number(group.numberOfBuckets) > 0;

  //       if ((hasCultivar && !hasBuckets) || (!hasCultivar && hasBuckets)) {
  //         hasHalfFilled = true;
  //       }

  //       if (hasCultivar && hasBuckets) {
  //         totalAssignedAnywhere += Number(group.numberOfBuckets);
  //       }
  //     });
  //   });

  //   if (hasHalfFilled) return true;
  //   if (totalAssignedAnywhere === 0) return true;

  //   return false;
  // };

  if (!open) return null;

  return (
    <Modal className="department-modal" open={open} onClose={onClose}>
      <div className="modal_block" style={{ maxWidth: 1000, width: "95vw" }}>
        <div className="modal_header">
          <div className="modal_title_wrapper">
            <h2 className="modal_title">{`Add Mapping for ${
              roomName || "Room"
            }`}</h2>
          </div>
          <div className="cancel-btn-wrap">
            <RxCross1 onClick={onClose} />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Tabs
            value={epicenterTabIndex}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            className="m-3"
            sx={{
              backgroundColor: "#fffffc",
              width: "100%",
              "& .MuiTabs-indicator": {
                backgroundColor: "#00A272",
                height: "3px",
              },
              "& .MuiTab-root": {
                color: "#A1A2A1",
                fontWeight: 400,
                fontSize: "16px",
                textTransform: "none",
                padding: "12px 20px",
                "&.Mui-selected": {
                  color: "#00A272",
                  fontWeight: 600,
                  backgroundColor: "#E6F6F1",
                  borderRadius: "4px 4px 0 0",
                },
              },
            }}
          >
            <Tab label="Epicenter 1" />
            <Tab label="Epicenter 2" />
            <Tab label="Epicenter 3" />
          </Tabs>

          <FormControl
            sx={{ minWidth: 120, marginRight: 2, marginBottom: "28px" }}
          >
            <label>Number of Buckets</label>
            <TextField
              value={bucketCount}
              InputProps={{ readOnly: true }}
              disabled
              size="small"
            />
          </FormControl>
          <FormControl
            sx={{ minWidth: 120, marginRight: 2, marginBottom: "28px" }}
          >
            <label># Clones Selected</label>
            <TextField
              value={(tabData[epicenterTab] || []).reduce(
                (sum, group) => sum + (group.selected?.length || 0),
                0,
              )}
              InputProps={{ readOnly: true }}
              disabled
              size="small"
            />
          </FormControl>
        </div>

        <Formik
          key={epicenterTabIndex}
          innerRef={formikRef}
          enableReinitialize
          initialValues={{}}
          validationSchema={validationSchema}
          onSubmit={() => {}}
        >
          {() => {
            // current epicenter groups
            const groups = tabData[epicenterTab] || [];
            const usedCloneIds = getUsedCloneIds();

            return (
              <Form>
                <div
                  className="modal_content"
                  style={{ maxHeight: "65vh", overflow: "auto" }}
                >
                  {groups.map((group, groupIndex) => (
                    <div
                      key={group.__localId}
                      style={{
                        padding: 12,
                        border: "1px solid #eee",
                        marginBottom: 12,
                        borderRadius: 6,
                      }}
                    >
                      {/* =========================================
                      OLD ROW LAYOUT - TRAYS & CLONES (COMMENTED OUT)
                      ========================================= */}
                      <div className="row" style={{ marginBottom: 12 }}>
                        <div className="col-md-5">
                          <div className="form_field_wrapper m-0">
                            <label>Cultivar Name</label>
                            <FormControl fullWidth>
                              <Select
                                value={group.cultivarId || ""}
                                onChange={(e) =>
                                  onGroupCultivarChange(
                                    epicenterTab,
                                    groupIndex,
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                renderValue={(val) => {
                                  if (!val) return <span>Select Cultivar</span>;
                                  const found = cultivars.find(
                                    (c) => String(c.id) === String(val),
                                  );
                                  return found ? found.name : val;
                                }}
                              >
                                <MenuItem value="">Select Cultivar</MenuItem>
                                {cultivars.map((c) => (
                                  <MenuItem key={c.id} value={String(c.id)}>
                                    {c.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="form_field_wrapper m-0">
                            <label>Tray ID</label>
                            <FormControl fullWidth>
                              <Select
                                multiple
                                value={group.trayId || []}
                                onChange={async (e) => {
                                  const value = e.target.value || [];
                                  // When the "all" option is used - keep behavior similar to before
                                  let newTrayIds = [];
                                  if (value[value.length - 1] === "all") {
                                    const allIds = (group.trays || []).map(
                                      (t) => String(t.id),
                                    );
                                    const isAllSelected =
                                      (group.trayId || []).length ===
                                        allIds.length && allIds.length > 0;
                                    newTrayIds = isAllSelected ? [] : allIds;
                                  } else {
                                    newTrayIds = value.map((v) => String(v));
                                  }
                                  // fetch options & filter selected accordingly
                                  await onGroupTrayChange(
                                    epicenterTab,
                                    groupIndex,
                                    newTrayIds,
                                  );
                                }}
                                displayEmpty
                                renderValue={(vals) => {
                                  if (!vals || vals.length === 0)
                                    return <span>Select Tray ID</span>;
                                  const labels = vals
                                    .map((val) => {
                                      const found = (group.trays || []).find(
                                        (t) => String(t?.id) === String(val),
                                      );
                                      return found ? found.tray_id : val;
                                    })
                                    .filter(Boolean);
                                  return labels.join(", ");
                                }}
                              >
                                {(group.trays || []).length > 0 && (
                                  <MenuItem key="select-all" value="all">
                                    <ListItemIcon>
                                      <Checkbox
                                        checked={
                                          (group.trayId || []).length ===
                                            (group.trays || []).length &&
                                          (group.trays || []).length > 0
                                        }
                                        indeterminate={
                                          (group.trayId || []).length > 0 &&
                                          (group.trayId || []).length <
                                            (group.trays || []).length
                                        }
                                      />
                                    </ListItemIcon>
                                    <ListItemText primary="Select All" />
                                  </MenuItem>
                                )}

                                {(group.trays || []).map((t) => {
                                  const idStr = String(t.id);
                                  const isSelectedHere = (
                                    group.trayId || []
                                  ).some((id) => String(id) === idStr);
                                  return (
                                    <MenuItem key={t.id} value={idStr}>
                                      <Checkbox checked={isSelectedHere} />
                                      <ListItemText primary={t.tray_id} />
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </div>
                        </div>
                        <div className="col-md-1">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              height: "100%",
                              justifyContent: "center",
                              marginTop: "12px",
                            }}
                          >
                            <RxCross1
                              onClick={() =>
                                removeCultivarGroup(epicenterTab, groupIndex)
                              }
                              style={{
                                color: "red",
                                cursor: "pointer",
                                fontSize: "16px",
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Clone list */}
                      <div
                        className="row"
                        style={{ maxHeight: 320, overflow: "auto" }}
                      >
                        {(group.options || [])
                          .filter(Boolean)
                          .filter((opt) => {
                            // hide clones selected in other groups, but allow if selected in this group
                            if (group.selected?.includes(opt.id)) return true;
                            return !usedCloneIds.has(opt.id);
                          })
                          .map((opt) => (
                            <div
                              key={opt.id}
                              className="col-md-4"
                              style={{ marginBottom: 8 }}
                            >
                              <label
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  alignItems: "center",
                                }}
                              >
                                <Checkbox
                                  checked={(group.selected || []).includes(
                                    opt.id,
                                  )}
                                  onChange={() =>
                                    toggleCloneForGroup(
                                      epicenterTab,
                                      groupIndex,
                                      opt.id,
                                    )
                                  }
                                />
                                {opt.clone_id}
                              </label>
                            </div>
                          ))}
                      </div>

                      {/* NEW ROW LAYOUT - CULTIVAR & BUCKET COUNT */}
                      {/* <div className="row" style={{ marginBottom: 12 }}>
                        <div className="col-md-6">
                          <div className="form_field_wrapper m-0">
                            <label>Cultivar Name</label>
                            <FormControl fullWidth>
                              <Select
                                value={group.cultivarId || ""}
                                onChange={(e) =>
                                  onGroupCultivarChange(
                                    epicenterTab,
                                    groupIndex,
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                renderValue={(val) => {
                                  if (!val) return <span>Select Cultivar</span>;
                                  const found = cultivars.find(
                                    (c) => String(c.id) === String(val),
                                  );
                                  return found ? found.name : val;
                                }}
                              >
                                <MenuItem value="">Select Cultivar</MenuItem>
                                {cultivars.map((c) => (
                                  <MenuItem key={c.id} value={String(c.id)}>
                                    {c.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        </div>

                        <div className="col-md-5">
                          <div className="form_field_wrapper m-0">
                            <label>Number of Buckets</label>
                            <FormControl fullWidth>
                              <TextField
                                type="number"
                                fullWidth
                                value={group.numberOfBuckets || ""}
                                onChange={(e) =>
                                  onGroupBucketsChange(
                                    epicenterTab,
                                    groupIndex,
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter count"
                                inputProps={{ min: 1 }}
                              />
                            </FormControl>
                          </div>
                        </div>

                        <div className="col-md-1">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              height: "100%",
                              justifyContent: "center",
                              marginTop: "12px",
                            }}
                          >
                            <RxCross1
                              onClick={() =>
                                removeCultivarGroup(epicenterTab, groupIndex)
                              }
                              style={{
                                color: "red",
                                cursor: "pointer",
                                fontSize: "16px",
                              }}
                            />
                          </div>
                        </div>
                      </div> */}
                    </div>
                  ))}

                  <div
                    className="float-right"
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "center",
                      marginTop: "15px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => addCultivarGroup(epicenterTab)}
                      className="btn btn-border flex"
                      style={{
                        display: "flex",
                        padding: "6px 12px",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "10px",
                        borderRadius: "4px",
                        border: "1px solid #C0C0C0",
                      }}
                    >
                      <IoMdAdd
                        className="text-sm"
                        style={{
                          width: "20",
                          height: "20px",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: 500,
                          lineHeight: "20px",
                          letterSpacing: "0em",
                        }}
                      >
                        Add
                      </span>
                    </button>
                  </div>
                </div>

                <div
                  className="modal_footer"
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "flex-end",
                    paddingTop: 12,
                  }}
                >
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn btn-border"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAssign}
                  >
                    Assign Mapping
                  </button>
                  {/* <button
                    type="button"
                    className={`btn btn-primary ${getIsDisabled() ? "disabled" : ""}`}
                    onClick={(e) => {
                      if (getIsDisabled()) {
                        e.preventDefault();
                        return;
                      }
                      handleAssign();
                    }}
                    style={{
                      opacity: getIsDisabled() ? 0.5 : 1,
                      cursor: getIsDisabled() ? "not-allowed" : "pointer",
                      pointerEvents: "auto",
                    }}
                  >
                    Assign Mapping
                  </button> */}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
}

export default AssignMappingModal;
